import { useCallback, useEffect, useRef, useState } from 'react'
import { getLastMessage } from '../ChatDrawer.hooks'

const scrollToBottom = (scrollRef: React.RefObject<HTMLDivElement>) => {
  if (scrollRef.current) {
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }
}

const isAtBottom = (scrollRef: React.RefObject<HTMLDivElement>, threshold: number = 10) => {
  if (!scrollRef.current) return true
  const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
  return scrollTop + clientHeight >= scrollHeight - threshold
}

export const useScrollToBottom = (messages: any[]) => {
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [hasNewMessages, setHasNewMessages] = useState(false)
  const messagesScrollRef = useRef<HTMLDivElement>(null)
  const lastMessageCountWhenScrolledRef = useRef(0)

  const handleScroll = useCallback(() => {
    const atBottom = isAtBottom(messagesScrollRef)

    if (!atBottom && !userHasScrolledUp) {
      // User has scrolled up for the first time
      setUserHasScrolledUp(true)
      setShowScrollButton(true)
      setHasNewMessages(false)
      lastMessageCountWhenScrolledRef.current = messages.length
    } else if (atBottom && userHasScrolledUp) {
      // User has scrolled back to bottom
      setUserHasScrolledUp(false)
      setShowScrollButton(false)
      setHasNewMessages(false)
    }
  }, [userHasScrolledUp, messages.length])

  useEffect(() => {
    const scrollElement = messagesScrollRef.current
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll)
      return () => {
        scrollElement.removeEventListener('scroll', handleScroll)
      }
    }
  }, [handleScroll])

  // Check for new messages when user has scrolled up
  useEffect(() => {
    if (userHasScrolledUp && messages.length > lastMessageCountWhenScrolledRef.current) {
      setHasNewMessages(true)
      setShowScrollButton(true)
    }
  }, [messages.length, userHasScrolledUp])

  // Auto-scroll to bottom when new messages arrive, but only if user hasn't scrolled up
  useEffect(() => {
    if (messages.length > 0 && messagesScrollRef.current && !userHasScrolledUp) {
      requestAnimationFrame(() => {
        if (messagesScrollRef.current) {
          scrollToBottom(messagesScrollRef)
        }
      })
    }
  }, [messages.length, getLastMessage(messages)?.content, userHasScrolledUp])

  const handleScrollToBottom = useCallback(() => {
    scrollToBottom(messagesScrollRef)
    setUserHasScrolledUp(false)
    setShowScrollButton(false)
    setHasNewMessages(false)
  }, [])

  return {
    messagesScrollRef,
    showScrollButton,
    hasNewMessages,
    handleScrollToBottom,
  }
}
